import React from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import { InlineMessage, INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';

import { FormattedCurrency } from '../../components/formatted-currency';
import InsuranceCheckboxWithLabel from './checkbox-with-label';
import InsuranceDropdownWithLabel from './dropdown-with-label';
import { InsuranceProviders } from '../../enums/insurance-providers';
import InsuranceTerms from '../insured-amount/insurance-terms';
import { useAvailableInsuranceOptions } from '../../services/insurance-service';
import { useGetInsuranceAlerts, maxInsuredValue } from '../hooks/use-get-insurance-alerts';
import { useFulfillmentPlannerContext } from '../context';
import { useIsLocalDeliveryServiceSelected } from '../hooks/use-is-local-delivery-service-selected';

import messages from './messages';
import style from './style.module.less';
import insuranceStyle from '../insured-amount/insurance-terms/style.module.css';

const Insurance = ({ fulfillmentPlanId, isPartOfInsurancePromoExperiment = false }) => {
  const { calculateRecommendedInsuranceValue, hasInsuranceProviderSelected, insuranceProvider, serviceIdAndSellerLabelProviderId } =
    useFulfillmentPlannerContext();
  const { selectedInsuranceOptionId, showHighValueWarning, isMaxCoverageExceeded, hasUnknownInsuranceProvider } = useGetInsuranceAlerts(
    calculateRecommendedInsuranceValue,
    insuranceProvider,
    hasInsuranceProviderSelected,
  );
  const isLocalDelivery = useIsLocalDeliveryServiceSelected();
  const insuranceOptions = useAvailableInsuranceOptions(serviceIdAndSellerLabelProviderId?.sellerLabelProviderId);

  if (isLocalDelivery) {
    return null;
  }

  const showCheckbox = insuranceOptions.find(
    (option) => option.name === InsuranceProviders.ParcelGuard.name || option.name === InsuranceProviders.XCover.name,
  );
  const showDropdown = insuranceOptions.find(
    (option) =>
      option.name === InsuranceProviders.Carrier.name ||
      option.name === InsuranceProviders.External.name ||
      option.name === InsuranceProviders.Shipsurance.name,
  );

  return (
    <React.Fragment>
      {!showCheckbox && !showDropdown && (
        <InlineMessage className={style.alert} type={INLINE_MESSAGE_TYPES.info}>
          <FormattedMessage {...messages.insuranceNotAvailable} />
        </InlineMessage>
      )}
      {showDropdown && (
        <InsuranceDropdownWithLabel
          fulfillmentPlanId={fulfillmentPlanId}
          insuranceOptions={insuranceOptions}
          isPartOfInsurancePromoExperiment={isPartOfInsurancePromoExperiment}
        />
      )}
      {showCheckbox && !showDropdown && <InsuranceCheckboxWithLabel />}
      {showHighValueWarning && (
        <InlineMessage className={style.alert} type={INLINE_MESSAGE_TYPES.info}>
          <FormattedMessage {...messages.highOrderValue} />
        </InlineMessage>
      )}
      {isMaxCoverageExceeded && (
        <InlineMessage className={style.alert} type={INLINE_MESSAGE_TYPES.warning}>
          <FormattedCurrency fractionDigits={0} value={maxInsuredValue}>
            {(formattedBalance) => (
              <FormattedMessage
                {...messages.maximumCoverage}
                values={{
                  amount: formattedBalance,
                }}
              />
            )}
          </FormattedCurrency>
        </InlineMessage>
      )}
      {hasUnknownInsuranceProvider && (
        <InlineMessage className={style.alert} type={INLINE_MESSAGE_TYPES.error}>
          <FormattedMessage
            {...messages.unknownInsuranceProvider}
            values={{
              insuranceProviderName: <FormattedMessage {...insuranceProvider.messageDescriptor} />,
            }}
          />
        </InlineMessage>
      )}
      {!hasUnknownInsuranceProvider && (
        <InsuranceTerms className={insuranceStyle.terms} selectedInsuranceOptionId={selectedInsuranceOptionId} />
      )}
    </React.Fragment>
  );
};

Insurance.propTypes = {
  fulfillmentPlanId: PropTypes.string.isRequired,
  isPartOfInsurancePromoExperiment: PropTypes.bool,
};

export default Insurance;
